import { CartAddEvent } from "@theme/events";

/**
 * Bundle builder.
 *
 * Adds individual "Toy Storage Box" products to the cart. There is deliberately
 * no bundle product: pricing comes from the three automatic discounts in the
 * Shopify admin, which fire on box quantity.
 *
 * TIERS MUST STAY IN SYNC with those discounts:
 *   4+  -> 3.97 off    6+ -> 5.95 off    8+ -> 7.93 off
 * Box prices are read from the DOM (rendered by Liquid), so they never drift.
 */
const TIERS = [
  { min: 8, off: 793, price: "£39.99" },
  { min: 6, off: 595, price: "£29.99" },
  { min: 4, off: 397, price: "£19.99" },
];
const FREE_DELIVERY_FROM = 2999;

const money = (pence) => "£" + (pence / 100).toFixed(2);
const discountFor = (n) => TIERS.find((t) => n >= t.min)?.off ?? 0;
const nextTierAbove = (n) => [...TIERS].reverse().find((t) => t.min > n) ?? null;

class BundleBuilder extends HTMLElement {
  connectedCallback() {
    this.target = parseInt(this.dataset.target || "4", 10);
    this.rows = [...this.querySelectorAll("[data-variant-id]")];
    this.summaryEl = this.querySelector("[data-bundle-summary]");
    this.priceEl = this.querySelector("[data-bundle-price]");
    this.wasEl = this.querySelector("[data-bundle-was]");
    this.noteEl = this.querySelector("[data-bundle-note]");
    this.fillEl = this.querySelector("[data-bundle-fill]");
    this.button = this.querySelector("[data-bundle-submit]");
    this.errorEl = this.querySelector("[data-bundle-error]");

    this.addEventListener("click", this.#onClick);
    this.addEventListener("input", this.#onInput);
    this.button?.addEventListener("click", this.#submit);
    this.#render();
  }

  #countFor(row) {
    return parseInt(row.querySelector("[data-qty-input]").value || "0", 10);
  }

  #total() {
    return this.rows.reduce((n, r) => n + this.#countFor(r), 0);
  }

  #subtotal() {
    return this.rows.reduce(
      (sum, r) => sum + this.#countFor(r) * parseInt(r.dataset.price || "0", 10),
      0
    );
  }

  #setQty(row, value) {
    const max = parseInt(row.dataset.max || "99", 10);
    const input = row.querySelector("[data-qty-input]");
    input.value = String(Math.min(Math.max(value, 0), max));
    row.classList.toggle("is-chosen", Number(input.value) > 0);
    this.#render();
  }

  #onClick = (event) => {
    const btn = event.target.closest("[data-qty-step]");
    if (!btn) return;
    event.preventDefault();
    const row = btn.closest("[data-variant-id]");
    const delta = btn.dataset.qtyStep === "up" ? 1 : -1;
    this.#setQty(row, this.#countFor(row) + delta);
  };

  #onInput = (event) => {
    const input = event.target.closest("[data-qty-input]");
    if (!input) return;
    this.#setQty(input.closest("[data-variant-id]"), parseInt(input.value || "0", 10));
  };

  #render() {
    const count = this.#total();
    const subtotal = this.#subtotal();
    const off = discountFor(count);
    const payable = subtotal - off;
    const next = nextTierAbove(count);

    this.summaryEl.textContent =
      count === 0
        ? `Choose ${this.target} boxes`
        : `${count} box${count === 1 ? "" : "es"} chosen`;

    this.priceEl.textContent = count === 0 ? "" : money(payable);
    this.wasEl.textContent = off > 0 ? money(subtotal) : "";
    this.wasEl.hidden = off === 0;

    const pct = Math.min((count / this.target) * 100, 100);
    this.fillEl.style.setProperty("--percent", pct + "%");

    let note = "";
    if (count === 0) {
      note = `Any ${this.target} boxes for ${TIERS.find((t) => t.min === this.target)?.price ?? ""}.`;
    } else if (count < this.target) {
      note = ""; // the button already reads "Choose N more"
    } else if (next) {
      const need = next.min - count;
      note = `Add ${need} more box${need === 1 ? "" : "es"} to get any ${next.min} for ${next.price}.`;
    } else {
      note = "You have our best price.";
    }
    if (count >= this.target && payable >= FREE_DELIVERY_FROM) {
      note += " Free UK delivery included.";
    }
    this.noteEl.textContent = note;
    this.noteEl.hidden = note === "";

    this.button.disabled = count < this.target;
    this.button.textContent =
      count < this.target
        ? `Choose ${this.target - count} more`
        : `Add ${count} box${count === 1 ? "" : "es"} to cart, ${money(payable)}`;
  }

  #submit = async (event) => {
    event.preventDefault();
    const items = this.rows
      .map((r) => ({ id: Number(r.dataset.variantId), quantity: this.#countFor(r) }))
      .filter((i) => i.quantity > 0);
    if (!items.length) return;

    this.button.disabled = true;
    this.errorEl.hidden = true;

    const sectionIds = [...document.querySelectorAll("cart-items-component")]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    try {
      const res = await fetch(window.FoxTheme.routes.cart_add_url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          items,
          sections: sectionIds.length ? sectionIds.join(",") : undefined,
        }),
      });
      const data = await res.json();

      if (data.status) {
        this.errorEl.textContent = data.description || data.message;
        this.errorEl.hidden = false;
        this.button.disabled = false;
        return;
      }

      const added = items.reduce((n, i) => n + i.quantity, 0);
      this.dispatchEvent(
        new CartAddEvent({}, this.id || "bundle-builder", {
          source: "bundle-builder",
          itemCount: added,
          sections: data.sections,
        })
      );

      this.rows.forEach((r) => this.#setQty(r, 0));
    } catch (e) {
      this.errorEl.textContent = "Something went wrong adding your bundle. Please try again.";
      this.errorEl.hidden = false;
      this.button.disabled = false;
    }
  };
}

customElements.define("bundle-builder", BundleBuilder);
